import { FC, useState } from "react";
import { dollars } from "./dollars";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { buildPriceDetails, PriceRow } from "./priceDetails";

const Row: FC<{ row: PriceRow; muted?: boolean }> = ({ row, muted }) => (
  <div className={`flex items-start justify-between gap-3 py-1 text-sm ${muted ? "text-text-color/70" : ""}`}>
    {/* Truncated: a long car name must not push the amount off the sheet. */}
    <div className="min-w-0 flex-1">
      <span className="block truncate" title={row.label}>{row.label}</span>
      {row.sub && <p className="truncate text-xs text-text-color/60">{row.sub}</p>}
    </div>
    <span className={`shrink-0 whitespace-nowrap text-right tabular-nums ${row.negative ? "font-semibold text-red-600" : ""}`}>
      {row.negative ? "-" : ""}
      {dollars(row.amount)}
    </span>
  </div>
);

/**
 * The price lines, the total and the deposit, built by buildPriceDetails() from the invoice the bill itself
 * renders, so they cannot disagree with the bill. The phone's "Price details" sheet (1NOW-11155) and the
 * desktop's "Booking overview" card (1NOW-11173) both draw this.
 */
interface LinesProps {
  invoice: any;
  carName?: string;
  heading?: string;
  /** Said while there is no total yet. */
  emptyText?: string;
}

/** The charges, discounts and fees: the part of the bill that can be long. */
export const PriceBody: FC<LinesProps> = ({
  invoice,
  carName,
  heading = "Rental charges",
  emptyText = "Choose your dates to see the price.",
}) => {
  const [feesOpen, setFeesOpen] = useState(false);
  const d = buildPriceDetails(invoice, carName);
  if (d.error)
    return (
      <p className="rounded-xl border border-secondary-100 p-3 text-sm">We couldn&apos;t price this trip. {d.error}</p>
    );
  if (d.total === null) return <p className="py-4 text-sm text-text-color/70">{emptyText}</p>;
  return (
    <>
      {heading && (
        <p className="mt-1 text-xs font-extrabold uppercase tracking-wide text-text-color/70">{heading}</p>
      )}
      <div className="mt-1">
        {d.charges.map((r) => (
          <Row key={r.key} row={r} />
        ))}
        {d.discounts.map((r) => (
          <Row key={r.key} row={r} />
        ))}
      </div>
      {d.fees.rows.length > 0 && (
        <div className="mt-2 border-t border-secondary-100 pt-2">
          <button
            type="button"
            onClick={() => setFeesOpen((o) => !o)}
            aria-expanded={feesOpen}
            className="flex w-full items-center justify-between gap-3 py-1 text-sm font-bold"
          >
            <span className="flex items-center gap-1">
              Taxes and fees
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${feesOpen ? "rotate-180" : ""}`} />
            </span>
            <span className="tabular-nums">{dollars(d.fees.total)}</span>
          </button>
          {feesOpen && d.fees.rows.map((r) => <Row key={r.key} row={r} muted />)}
        </div>
      )}
    </>
  );
};

/**
 * The desktop card's price lines (1NOW-11173): one "Price details" row with the number of lines, closed to
 * begin with, and a chevron that opens them in place. Nothing in the card scrolls on its own: an open list
 * makes the card taller, and the page scrolls.
 */
export const PriceDetailsToggle: FC<Pick<LinesProps, "invoice" | "carName" | "emptyText">> = ({
  invoice,
  carName,
  emptyText,
}) => {
  const [open, setOpen] = useState(false);
  const d = buildPriceDetails(invoice, carName);
  // No quote (or none that could be made): the message, not a row that opens onto nothing.
  if (d.error || d.total === null) return <PriceBody invoice={invoice} carName={carName} emptyText={emptyText} />;
  const lines = d.charges.length + d.discounts.length + (d.fees.rows.length > 0 ? 1 : 0);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 py-1 text-sm font-extrabold"
      >
        <span>
          Price details{" "}
          <span className="font-semibold text-text-color/70">
            ({lines} {lines === 1 ? "line" : "lines"})
          </span>
        </span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && <PriceBody invoice={invoice} carName={carName} heading="" />}
    </>
  );
};

/** The total and the deposit: what the desktop card keeps in view above its button. */
export const PriceTotal: FC<Pick<LinesProps, "invoice" | "carName">> = ({ invoice, carName }) => {
  const d = buildPriceDetails(invoice, carName);
  if (d.error || d.total === null) return null;
  return (
    <>
      <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-secondary-100 pt-3">
        <span className="text-sm font-extrabold">Total{d.hasTax ? " (incl. tax)" : ""}</span>
        <span className="text-2xl font-extrabold tabular-nums" data-testid="price-total">
          {dollars(d.total)}
        </span>
      </div>
      {d.deposit !== null && (
        <div className="mt-3 rounded-xl bg-secondary-100/40 p-3 text-sm" data-testid="price-deposit">
          <b>Refundable deposit {dollars(d.deposit)}</b>
          <p className="text-xs text-text-color/70">Held on your card during the trip, not part of the total.</p>
        </div>
      )}
    </>
  );
};

const PriceLines: FC<LinesProps> = (p) => (
  <>
    <PriceBody {...p} />
    <PriceTotal invoice={p.invoice} carName={p.carName} />
  </>
);

export default PriceLines;
